import { supabase } from "@/lib/supabase/supabaseClient";
import { store } from "@/lib/states/store";
import type {
  AiStreamEvent,
  AiToolCall,
  AddExerciseArgs,
  RemoveExerciseArgs,
  ReplaceExerciseArgs,
  UpdateExerciseArgs,
} from "./types";

type CurrentExercise = {
  ex_order: number;
  name: string;
  tracking_type: string;
  sets?: Array<{
    startRange?: number;
    endRange?: number;
    targetTimeSeconds?: number;
    targetDistanceMeters?: number;
  }>;
};

type StreamParams = {
  prompt: string;
  currentWorkout: CurrentExercise[];
  onEvent: (event: AiStreamEvent) => void;
  signal?: AbortSignal;
};

const isAbortError = (err: unknown): boolean =>
  err instanceof Error && err.name === "AbortError";

type PartialToolCall = {
  index: number;
  id: string;
  name: string;
  argsJson: string;
};

const parseToolCallArgs = (
  name: string,
  argsJson: string,
): AiToolCall | null => {
  let parsed: unknown;
  try {
    parsed = argsJson.trim() ? JSON.parse(argsJson) : {};
  } catch {
    return null;
  }

  if (name === "add_exercise") {
    return { name: "add_exercise", args: parsed as AddExerciseArgs };
  }
  if (name === "remove_exercise") {
    return { name: "remove_exercise", args: parsed as RemoveExerciseArgs };
  }
  if (name === "replace_exercise") {
    return { name: "replace_exercise", args: parsed as ReplaceExerciseArgs };
  }
  if (name === "update_exercise") {
    return { name: "update_exercise", args: parsed as UpdateExerciseArgs };
  }
  if (name === "no_workout_change") {
    return { name: "no_workout_change", args: {} };
  }
  return null;
};

const apiOrigin = () =>
  (import.meta.env.VITE_API_URL ?? "http://127.0.0.1:8790").replace(/\/$/, "");

export async function streamWorkoutBuilder({
  prompt,
  currentWorkout,
  onEvent,
  signal,
}: StreamParams): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    onEvent({ type: "error", message: "Not authenticated" });
    return;
  }

  const orgId = store.getState().session.org?.id;
  if (!orgId) {
    onEvent({ type: "error", message: "No organisation selected" });
    return;
  }

  let response: Response;
  try {
    response = await fetch(`${apiOrigin()}/ai/workout-builder`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
        "x-org-id": orgId,
      },
      body: JSON.stringify({ prompt, currentWorkout }),
      signal,
    });
  } catch (err) {
    if (isAbortError(err)) return;
    onEvent({
      type: "error",
      message: err instanceof Error ? err.message : "Network error",
    });
    return;
  }

  if (!response.ok) {
    let errorMessage = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      if (typeof body.error === "string") errorMessage = body.error;
    } catch {
      // ignore
    }
    onEvent({ type: "error", message: errorMessage });
    return;
  }

  if (!response.body) {
    onEvent({ type: "error", message: "No response body" });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const partialToolCalls = new Map<number, PartialToolCall>();

  const flushToolCalls = () => {
    for (const [, partial] of partialToolCalls) {
      const call = parseToolCallArgs(partial.name, partial.argsJson);
      if (call) {
        onEvent({ type: "tool_call_complete", call });
      }
    }
    partialToolCalls.clear();
  };

  const processLine = (line: string) => {
    if (!line.startsWith("data: ")) return;
    const data = line.slice(6).trim();

    if (data === "[DONE]") {
      if (partialToolCalls.size > 0) flushToolCalls();
      onEvent({ type: "done" });
      return;
    }

    let chunk: unknown;
    try {
      chunk = JSON.parse(data);
    } catch {
      return;
    }

    const choice = (chunk as { choices?: unknown[] })?.choices?.[0] as
      | {
          delta?: {
            content?: string | null;
            tool_calls?: Array<{
              index: number;
              id?: string;
              function?: { name?: string; arguments?: string };
            }>;
          };
          finish_reason?: string | null;
        }
      | undefined;

    if (!choice) return;

    const delta = choice.delta;
    if (delta?.content) {
      onEvent({ type: "text_delta", content: delta.content });
    }

    if (delta?.tool_calls) {
      for (const tc of delta.tool_calls) {
        const idx = tc.index;
        if (!partialToolCalls.has(idx)) {
          partialToolCalls.set(idx, {
            index: idx,
            id: tc.id ?? "",
            name: tc.function?.name ?? "",
            argsJson: "",
          });
        }
        const partial = partialToolCalls.get(idx)!;
        if (tc.id) partial.id = tc.id;
        if (tc.function?.name && partial.name === "") partial.name = tc.function.name;
        if (tc.function?.arguments) partial.argsJson += tc.function.arguments;
      }
    }

    if (choice.finish_reason === "tool_calls" || choice.finish_reason === "stop") {
      flushToolCalls();
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";
      for (const line of lines) {
        processLine(line.trim());
      }
    }

    if (buffer.trim()) {
      processLine(buffer.trim());
    }
  } catch (err) {
    if (isAbortError(err)) return;
    onEvent({
      type: "error",
      message: err instanceof Error ? err.message : "Stream read error",
    });
  } finally {
    reader.releaseLock();
  }
};
